import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {SendersFormatterProps} from '../sendersFormatter';
import {
  formatSenderName,
  makeEllipsisElement,
  SenderStyleProps,
  shrinkFlexItemFirst,
  shrinkFlexItemLast,
  StyledLeftPaddedSpan,
  StyledSenderBase,
} from './common';

const StyledSendersWrapper = styled.span`
  display: flex;
`;

const StyledSingleSenderSpan = styled(StyledLeftPaddedSpan)<SenderStyleProps>`
  ${ellipsis()};
`;

const StyledFirstSenderSpan = styled(StyledLeftPaddedSpan)<SenderStyleProps>`
  ${ellipsis()}

  ${shrinkFlexItemLast()}
`;

const StyledLastSenderSpan = styled(StyledLeftPaddedSpan)<SenderStyleProps>`
  ${ellipsis()}

  ${shrinkFlexItemFirst()}
`;

/*
 * Component.
 */

export const ConversationSendersFormatter: FC<SendersFormatterProps> = (props) => {
  const {conversationSenders, isCellSelected} = props;

  const {firstSender, lastSender, isComplete, isSearchMatchingOnOtherSenders} = conversationSenders;

  // If the first and last sender are the same, display a unique sender.
  // Give priority to the last sender – it is more relevant for notification emails.
  // See #11972.
  if (isComplete && lastSender && lastSender.key === firstSender?.key) {
    return (
      <StyledSendersWrapper>
        <StyledSingleSenderSpan $hasHighlight={isDefined(lastSender.highlight)} $isSelected={isCellSelected}>
          {formatSenderName(lastSender, isCellSelected, {useLongName: true})}
        </StyledSingleSenderSpan>
      </StyledSendersWrapper>
    );
  }

  const haveSameFirstName =
    firstSender &&
    lastSender &&
    !firstSender.isMe &&
    !lastSender.isMe &&
    firstSender.key !== lastSender.key &&
    firstSender.firstName === lastSender.firstName;

  // FIXME(ldemarne): Handle the undefined sender case.
  const firstSenderName = firstSender
    ? formatSenderName(firstSender, isCellSelected, {
        showFirstCharOfLastName: haveSameFirstName || isDefined(firstSender.highlight),
      })
    : '';

  const lastSenderName = lastSender
    ? formatSenderName(lastSender, isCellSelected, {
        showFirstCharOfLastName: haveSameFirstName || isDefined(lastSender.highlight),
        useLongName: true,
      })
    : '';

  const ellipsisOrComma = isComplete ? (
    <StyledSenderBase key="comma" $hasHighlight={false} $isSelected={isCellSelected}>
      ,
    </StyledSenderBase>
  ) : (
    makeEllipsisElement(isCellSelected, isSearchMatchingOnOtherSenders)
  );

  return (
    <StyledSendersWrapper>
      <StyledFirstSenderSpan
        key="firstSender"
        $hasHighlight={isDefined(firstSender?.highlight)}
        $isSelected={isCellSelected}
      >
        {firstSenderName}
        {ellipsisOrComma}
      </StyledFirstSenderSpan>
      <StyledLastSenderSpan
        key="lastSender"
        $hasHighlight={isDefined(lastSender?.highlight)}
        $isSelected={isCellSelected}
      >
        {lastSenderName}
      </StyledLastSenderSpan>
    </StyledSendersWrapper>
  );
};
